<h2 mat-dialog-title class="dlg-title">
  <span class="dlg-heading">Risk Assessment</span>
  @if (!loading) {
    <label class="setting-inline-label" for="risk-setting-select">Setting:</label>
    <select id="risk-setting-select" class="setting-select"
            [(ngModel)]="selectedKey" [ngModelOptions]="{standalone: true}"
            (ngModelChange)="onSelectionChange($event)">
      <option [ngValue]="null" disabled>— Select —</option>
      @for (option of options; track option.key) {
        <option [ngValue]="option.key">{{ option.label }}</option>
      }
    </select>
    @if (isSuperseded) { <span class="status-chip chip-superseded">Superseded</span> }
    @else if (currentSet?.submitted) { <span class="status-chip chip-submitted">Submitted</span> }
    @else if (currentSet) { <span class="status-chip chip-draft">Draft</span> }
    @else { <span class="status-chip chip-new">Not started</span> }
  }
</h2>

<mat-dialog-content class="dlg-body">
  @if (loading) {
    <p class="tw:text-center tw:my-8">Loading risk assessment…</p>
  } @else {

    @if (isSuperseded) {
      <div class="banner banner-superseded">
        <span>📄</span>
        <span class="tw:flex-1">Superseded by version {{ supersededByVersion }}. Submitted
          {{ currentSet.submittedDate | date:'d MMM yyyy' }} — kept for the record.</span>
      </div>
    } @else if (readonly) {
      <div class="banner banner-locked">
        <span>🔒</span>
        <span class="tw:flex-1">Submitted {{ currentSet.submittedDate | date:'d MMM yyyy' }}. This
          assessment is locked — you can: start a new assessment, unlock to make corrections or start a new risk assessment in a similar setting.</span>
        @if (canStartNew) {
          <button mat-stroked-button [disabled]="saving" (click)="startNew()">New</button>
        }
        <button mat-stroked-button (click)="unlock()">Unlock</button>
      </div>
    }

    @if (errorBannerText) {
      <div class="banner banner-error">⚠ {{ errorBannerText }}</div>
    }

    @if (currentSettingId == null) {
      <p class="setting-hint">One assessment per setting, kept as versions. Pick a setting above to start
        or continue its assessment, or an earlier version to view it.</p>
    }

    @if (currentSettingId != null) {
      @if (!readonly && hasSavedAnswers) {
        <div class="progress-line">
          <div class="progress-track">
            <div class="progress-fill"
                 [style.width.%]="standardRows.length ? (answeredCount / standardRows.length) * 100 : 0"></div>
          </div>
          <span class="progress-label">{{ answeredCount }} of {{ standardRows.length }} answered</span>
        </div>
      }

      <div class="section-h">Risks for this setting</div>
      @for (row of standardRows; track row.riskDefinitionId) {
        <div class="risk-card" [class.risk-invalid]="rowInvalid(row)">
          <div class="risk-main">
            <div class="risk-text">
              <div class="risk-desc">{{ row.riskDescription }}
                @if (row.riskLevel) {
                  <span class="lvl" [class.lvl-low]="row.riskLevel === 'Low'"
                        [class.lvl-medium]="row.riskLevel === 'Medium'"
                        [class.lvl-high]="row.riskLevel === 'High'">{{ row.riskLevel }}</span>
                }
              </div>
              <button type="button" class="measures-toggle" (click)="row.showMeasures = !row.showMeasures">
                {{ row.showMeasures ? 'Hide control measures ▴' : 'View control measures ▾' }}
              </button>
              @if (row.showMeasures) { <div class="measures">{{ row.controlMeasures }}</div> }
            </div>
            <div class="seg-wrap">
              <span class="seg-label">Measures cover this?</span>
              <div class="seg">
                <button type="button" [class.on-yes]="row.isCovered === true"
                        [disabled]="readonly" (click)="setAnswer(row, true)">Yes</button>
                <button type="button" [class.on-no]="row.isCovered === false"
                        [disabled]="readonly" (click)="setAnswer(row, false)">No</button>
              </div>
            </div>
          </div>
          @if (row.isCovered === false) {
            <div class="additional">
              <label>Additional control measures (required for "No")</label>
              <textarea rows="2" [(ngModel)]="row.additionalMeasures" [ngModelOptions]="{standalone: true}"
                        [readonly]="readonly"
                        placeholder="What extra measures will you put in place?"></textarea>
            </div>
          }
        </div>
      }

      <div class="section-h">Risks not listed above</div>
      @for (row of customRows; track $index) {
        <div class="risk-card risk-custom" [class.risk-invalid]="rowInvalid(row)">
          <div class="custom-head">
            <span class="risk-desc">Custom risk</span>
            @if (!readonly) {
              <button type="button" class="link-danger" (click)="removeCustomRow($index)">Remove</button>
            }
          </div>
          <div class="custom-grid">
            <div class="field">
              <label>Risk description</label>
              <input type="text" [(ngModel)]="row.riskDescription" [ngModelOptions]="{standalone: true}"
                     [readonly]="readonly" placeholder="Describe the risk" />
            </div>
            <div class="field">
              <label>Control measures</label>
              <textarea rows="2" [(ngModel)]="row.controlMeasures" [ngModelOptions]="{standalone: true}"
                        [readonly]="readonly" placeholder="How is this risk managed?"></textarea>
            </div>
            <div class="seg-wrap">
              <span class="seg-label">Measures cover this?</span>
              <div class="seg">
                <button type="button" [class.on-yes]="row.isCovered === true"
                        [disabled]="readonly" (click)="setAnswer(row, true)">Yes</button>
                <button type="button" [class.on-no]="row.isCovered === false"
                        [disabled]="readonly" (click)="setAnswer(row, false)">No</button>
              </div>
            </div>
            @if (row.isCovered === false) {
              <div class="field">
                <label class="label-required">Additional control measures (required for "No")</label>
                <textarea rows="2" [(ngModel)]="row.additionalMeasures" [ngModelOptions]="{standalone: true}"
                          [readonly]="readonly"
                          placeholder="What extra measures will you put in place?"></textarea>
              </div>
            }
          </div>
        </div>
      }
      @if (!readonly) {
        <button type="button" class="btn-add" (click)="addCustomRow()">＋ Add a risk</button>
      }

      <div class="declaration">
        @if (!readonly) {
          <label class="declaration-label">
            <input type="checkbox" [(ngModel)]="declarationAccepted" [ngModelOptions]="{standalone: true}" />
            <span>I confirm that I have completed this risk assessment in full and that, to the best of
              my knowledge, all information provided is accurate, complete, and up to date. I understand
              that it is my responsibility to report any changes that may affect the risks identified or
              the control measures outlined in this assessment.</span>
          </label>
        } @else if (currentSet?.declarationAccepted) {
          <p class="declaration-accepted">✓ Declaration accepted on submission
            {{ currentSet.declarationAcceptedDate | date:'d MMM yyyy' }}.</p>
        }
      </div>
    }
  }
</mat-dialog-content>

<mat-dialog-actions class="dlg-foot">
  <span class="foot-note tw:flex-1">
    @if (lastSavedAt) { Draft saved {{ lastSavedAt | date:'HH:mm' }} }
  </span>
  @if (pdfEnabled && currentSet) {
    <button mat-stroked-button data-test="download-pdf" [disabled]="downloadingPdf"
            [title]="readonly ? '' : 'The PDF shows the assessment as last saved'"
            (click)="downloadPdf()">Download PDF</button>
  }
  <button mat-button (click)="close()">Close</button>
  @if (!readonly && currentSettingId != null) {
    <button mat-stroked-button [disabled]="saving" (click)="save()">Save draft</button>
    <button mat-raised-button color="primary" [disabled]="saving || !declarationAccepted"
            [title]="declarationAccepted ? '' : 'Accept the declaration to submit'"
            (click)="submit()">Submit</button>
  }
</mat-dialog-actions>
